import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  dailyReportFromHistoryItem,
  employeeApi,
  voiceRecordsApi,
  type DailyReport,
  type VoiceRecord,
} from '../api/client';
import { useTranscriptionEnabled } from '../hooks/useTranscriptionEnabled';
import { formatDayLabel, isValidDate, localDayRange } from '../utils/dayHistory';

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

/**
 * その日の結果（課題 #156。2026-10-02 高尾さん依頼）。振り返り履歴の日付から開く。
 *
 * 今のダッシュボードの日次の表示と同じ見え方で、その日の「コンディション傾向」（5 項目）・
 * 対話のトーン・前日比・AI 振り返りコメントと、その日の録音を出す。見られるのは本人だけ
 * （使う API がどちらも本人の分しか返さない）。
 * 文字起こしは、#99 の設定で「行う」の方の、テキストが返った録音の分だけ出す（行わない方には欄も出さない）。
 */
export default function DayResult() {
  const { t } = useTranslation();
  const { date } = useParams<{ date: string }>();
  const { getToken } = useAuth();
  const transcriptionEnabled = useTranscriptionEnabled();
  const [report, setReport] = useState<DailyReport | null>(null);
  const [records, setRecords] = useState<VoiceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const validDate = isValidDate(date);

  const load = useCallback(async () => {
    const token = getToken();
    if (!token || !validDate) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const range = localDayRange(date);
      const [item, recs] = await Promise.all([
        employeeApi.getReportForDate(token, date),
        // 録音の一覧が読めなくても、その日の結果は出す
        voiceRecordsApi.getList(token, { from: range.from, to: range.to, limit: 50 }).catch(() => null),
      ]);
      setReport(item ? dailyReportFromHistoryItem(item) : null);
      const list = recs?.data ?? [];
      setRecords(
        [...list].sort((a, b) => (a.recorded_at ?? a.created_at ?? '').localeCompare(b.recorded_at ?? b.created_at ?? ''))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [date, getToken, t, validDate]);

  useEffect(() => {
    load();
  }, [load]);

  const back = (
    <p className="report-history-back">
      <Link to="/dashboard/history">{t('common.back')} → {t('history.title')}</Link>
    </p>
  );

  if (!validDate) {
    return (
      <div className="page score-page day-result-page">
        <h1>{t('dayResult.titleUnknown')}</h1>
        <p className="score-error" role="alert">
          {t('dayResult.invalidDate')}
        </p>
        {back}
      </div>
    );
  }

  const isNew = report?.schema === 'new';
  const coldStart = report?.coldStart;
  const domains = report?.domains;
  const calm = report?.calm;
  const dayOverDay = report?.dayOverDay;
  const shownRecords = records;

  return (
    <div className="page score-page day-result-page">
      <h1>{t('dayResult.title', { date: formatDayLabel(date) })}</h1>

      {loading && <p>{t('common.loading')}</p>}

      {error && (
        <div className="score-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {!loading && !error && !report && (
        <div className="score-card">
          <p className="score-text" data-testid="day-result-empty">
            {t('dayResult.noReport')}
          </p>
        </div>
      )}

      {!loading && !error && report && (
        <>
          {isNew && domains && (coldStart?.show_4_domains ?? true) && (
            <div className="score-card" data-testid="day-result-domains">
              {/* 過去の日なので「今日の」とは言わない */}
              <h3>{t('dayResult.domainsTitle')}</h3>
              <div className="score-domains-grid">
                <div className="score-domain">
                  <div className="score-domain-label">{t('dashboard.domains.vitality')}</div>
                  <div className="score-stat">{Math.round(domains.vitality)}</div>
                </div>
                <div className="score-domain">
                  <div className="score-domain-label">{t('dashboard.domains.tension')}</div>
                  <div className="score-stat">{Math.round(domains.tension)}</div>
                </div>
                <div className="score-domain">
                  <div className="score-domain-label">{t('dashboard.domains.cognition')}</div>
                  <div className="score-stat">{Math.round(domains.cognition)}</div>
                  <div className="score-domain-meta">
                    {t('dashboard.domains.cognitionModeLabel')}: {domains.cognition_mode}
                  </div>
                </div>
                <div className="score-domain">
                  <div className="score-domain-label">{t('dashboard.domains.emotionStability')}</div>
                  <div className="score-stat">{Math.round(domains.emotion_stability)}</div>
                </div>
              </div>
            </div>
          )}

          {isNew && calm && (coldStart?.show_calm ?? true) && (
            <div className={`score-card score-calm score-calm-${calm.label}`} data-testid="day-result-calm">
              <h3>{t('dashboard.calm.title')}</h3>
              <p className="score-text">
                {t(`dashboard.calm.level${calm.level}`)} ／{' '}
                {t('dashboard.calm.atmosphereAvg', { value: Math.round(calm.atmosphere_avg) })}
              </p>
            </div>
          )}

          {isNew && dayOverDay && (coldStart?.show_day_over_day ?? true) && (
            <div className="score-card" data-testid="day-result-dod">
              <h3>{t('dashboard.dayOverDay.title')}</h3>
              <p className="score-text">
                {dayOverDay.direction === 'up' && t('dashboard.dayOverDay.up')}
                {dayOverDay.direction === 'down' && t('dashboard.dayOverDay.down')}
                {dayOverDay.direction === 'flat' && t('dashboard.dayOverDay.flat')}
                {dayOverDay.direction == null && t('dashboard.dayOverDay.unknown')}
                {dayOverDay.direction != null && typeof dayOverDay.delta_pct === 'number' && (
                  <>
                    {' '}
                    ({dayOverDay.delta_pct >= 0 ? '+' : ''}
                    {dayOverDay.delta_pct.toFixed(1)}%)
                  </>
                )}
              </p>
            </div>
          )}

          {!isNew && report.condition_emoji && (
            <div className="score-card">
              <h3>{t('dashboard.conditionToday')}</h3>
              <p className="score-text">{report.condition_emoji}</p>
            </div>
          )}

          <div className="score-card" data-testid="day-result-comment">
            <h3>{t('dashboard.aiComment')}</h3>
            <p className="score-text day-result-comment">{report.comment || t('dayResult.noComment')}</p>
          </div>
        </>
      )}

      {!loading && !error && (
        <div className="score-card" data-testid="day-result-recordings">
          <h3>{t('dayResult.recordingsTitle')}</h3>
          {shownRecords.length === 0 ? (
            <p className="score-text">{t('dayResult.noRecordings')}</p>
          ) : (
            <ol className="day-result-recordings">
              {shownRecords.map((r, i) => {
                const text = typeof r.transcript_text === 'string' ? r.transcript_text.trim() : '';
                return (
                  <li key={r.id ?? i} data-testid="day-result-recording">
                    <span className="day-result-recording-time">{formatTime(r.recorded_at ?? r.created_at)}</span>
                    {/* 文字起こしは「行う」の方の、テキストが返った録音の分だけ（#99。行わない方には欄も出さない） */}
                    {transcriptionEnabled && text && (
                      <details className="day-result-transcript" data-testid="day-result-transcript">
                        <summary>{t('record.transcriptTitle')}</summary>
                        <pre className="record-transcript-body">{text}</pre>
                      </details>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {back}
    </div>
  );
}
